<template>
  <div ref="reportBody" class="training-report">
    <div class="report-toolbar">
      <strong>测评报告</strong>
      <el-button type="text" icon="el-icon-chat-dot-round" @click="$emit('conversation', [])">查看对练记录</el-button>
    </div>
    <div v-if="!selectedCode" class="report-paper">
      <div class="score-summary">
        <div><div class="score-label">最终得分</div><div class="score-value">{{ scoreText(report.totalScore) }}<span>/ 100</span></div></div>
        <div v-if="resultKnown" class="score-stamp" :class="{ 'score-stamp-pass': report.passed }">{{ report.passed ? '已通过' : '未通过' }}</div>
        <span v-else class="muted">通关结果未记录</span>
      </div>
      <div class="score-rule">
        基础得分 {{ scoreText(report.baseScore) }} / 80 + 难度分 {{ scoreText(report.difficultyScore) }} / 20
        <div><span v-if="report.passScore != null">通关线 {{ scoreText(report.passScore) }} 分</span><span v-else>当次通关线未记录</span> · {{ modeText }}<span v-if="report.tierName"> · {{ report.tierName }}</span></div>
      </div>
      <p v-if="historical" class="historical-tip">历史报告保留当次评分结果；未记录的分类、扣分明细及对话引用不作补写。<span v-if="report.snapshotState === 'legacy_unavailable'">当次评分规则快照缺失，通关结果沿用原记录。</span></p>
      <div class="overview-columns">
        <section class="score-groups" aria-label="评分分类总览">
          <button
            v-for="(group, index) in groups" :key="group.code + ':' + index" class="score-group"
            type="button" :data-group-code="group.code" @click="selectGroup(group.code)"
          >
            <i :class="groupIcon(group.code)" aria-hidden="true" />
            <span class="group-label">{{ group.label || '评分分类' }}</span>
            <span class="group-score">{{ scoreText(group.score) }} <small>/ {{ scoreText(group.maxScore) }}</small></span>
            <span class="group-rate">{{ percentText(group) }}</span><i class="el-icon-arrow-right" aria-hidden="true" />
          </button>
          <p v-if="!groups.length" class="muted empty-text">本报告暂无评分明细</p>
        </section>
        <section class="coach">
          <h3>AI 教练评语</h3>
          <template v-if="coachSections.length">
            <div v-for="(section, index) in coachSections" :key="section.code || index" class="coach-section">
              <h4>{{ section.label || '教练反馈' }}</h4>
              <p v-if="section.comment">{{ section.comment }}</p>
              <p v-for="(suggestion, suggestionIndex) in textSuggestions(section.suggestions)" :key="suggestionIndex" class="coach-suggestion">{{ suggestion }}</p>
              <p v-if="!section.comment && !textSuggestions(section.suggestions).length" class="muted">本项暂未提供教练反馈</p>
            </div>
          </template>
          <template v-else>
            <p v-if="report.coachComment">{{ report.coachComment }}</p>
            <h4 v-if="suggestions.length">提升建议</h4>
            <p v-for="(suggestion, index) in suggestions" :key="index" class="coach-suggestion">{{ index + 1 }}. {{ suggestion }}</p>
            <p v-if="!report.coachComment && !suggestions.length" class="muted">本报告暂未提供教练反馈</p>
          </template>
        </section>
      </div>
      <section v-if="hasRadar" class="overview-radar">
        <h3>表达能力</h3>
        <report-radar :data="radarData" :max="radarMax" />
        <el-button
          v-if="expressionGroup" class="expression-link" plain
          size="small" @click="selectGroup(expressionGroup.code)"
        >查看表达能力详情 <i class="el-icon-arrow-right" /></el-button>
      </section>
    </div>
    <template v-else>
      <div class="report-navigation">
        <el-button type="text" icon="el-icon-arrow-left" class="overview-link" @click="selectGroup('')">返回总览</el-button>
        <div class="category-tabs" role="tablist" aria-label="评分分类">
          <button
            v-for="(group, index) in groups" :key="group.code + ':' + index" type="button"
            role="tab" :aria-selected="group.code === selectedCode" :data-group-code="group.code"
            :class="{ 'category-active': group.code === selectedCode }" @click="selectGroup(group.code)"
          >{{ group.label || '评分分类' }}</button>
        </div>
      </div>
      <section v-if="selectedGroup" class="report-details">
        <h3 class="detail-heading"><span><i :class="groupIcon(selectedGroup.code)" aria-hidden="true" /> {{ selectedGroup.label || '评分分类' }}</span><strong>{{ scoreText(selectedGroup.score) }} <small>/ {{ scoreText(selectedGroup.maxScore) }}</small></strong></h3>
        <p v-if="historical" class="historical-tip">本报告未记录结构化分类与扣分明细，以下为当次保存的原始评分项。</p>
        <div v-if="selectedCode === 'process' && flowSteps.length" class="flow-card">
          <h4>流程完成情况</h4>
          <div class="flow-row flow-head"><span>排序</span><span>考核点</span><span>完成情况</span></div>
          <div v-for="(step, index) in visibleFlowSteps" :key="index" class="flow-row">
            <span>{{ step.order == null ? index + 1 : step.order }}</span>
            <span>{{ step.label || '流程考核点' }}<el-button
              v-if="toArray(step.messageIds).length" type="text" size="mini"
              class="flow-link" @click="$emit('conversation', toArray(step.messageIds))"
            >查看对话</el-button></span>
            <span :class="{ 'flow-missing': step.completed === false }">{{ step.completed === true ? '已完成' : step.completed === false ? '未完成' : '未记录' }}</span>
          </div>
          <el-button
            v-if="flowSteps.length > 3" class="flow-toggle" plain
            size="small" @click="showAllFlow = !showAllFlow"
          >{{ showAllFlow ? '收起' : '查看全部' }} <i :class="showAllFlow ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" /></el-button>
        </div>
        <template v-if="selectedCode === 'confidence'">
          <div v-if="hasRadar" class="radar-card"><report-radar :data="radarData" :max="radarMax" /></div>
          <div v-for="(dimension, index) in radarDetails" :key="dimension.key || index" class="dimension-card">
            <div class="dimension-heading"><strong>{{ dimension.key }}</strong><span>{{ scoreText(dimension.score) }} <small>/ {{ scoreText(dimension.maxScore) }}</small></span></div>
            <p v-if="dimension.comment">{{ dimension.comment }}</p>
            <template v-if="textSuggestions(dimension.suggestions).length"><h4>提升建议</h4><p v-for="(suggestion, suggestionIndex) in textSuggestions(dimension.suggestions)" :key="suggestionIndex">{{ suggestion }}</p></template>
            <p v-if="!dimension.comment && !textSuggestions(dimension.suggestions).length" class="muted">本维度暂未提供详细评语</p>
          </div>
        </template>
        <score-point v-for="(item, index) in objectRows(selectedGroup.items)" :key="(item.itemId || 'item') + ':' + index" :item="item" @conversation="$emit('conversation', $event)" />
        <p v-if="!objectRows(selectedGroup.items).length" class="muted empty-text">本分类暂无评分项明细</p>
      </section>
    </template>
  </div>
</template>

<script>
import { scoreText, textSuggestions, toArray, toObject } from '@/utils/ai-train-record'
import ScorePoint from './ScorePoint'
import ReportRadar from './ReportRadar'

// 展示规则对齐 App Report.vue；此组件只读，不发请求、不重新计算评分。
export default {
  name: 'TrainingReport',
  components: { ScorePoint, ReportRadar },
  props: { report: { type: Object, required: true } },
  data() { return { selectedCode: '', showAllFlow: false } },
  computed: {
    historical() { return Number(this.report.reportVersion || 1) < 2 },
    resultKnown() { return typeof this.report.passed === 'boolean' },
    modeText() { return { 1: '练习', 2: '考试' }[this.report.mode] || '模式未记录' },
    groups() {
      const structured = this.objectRows(this.report.groups).filter(group => group.code)
      if (structured.length) {
        const order = ['content', 'process', 'confidence', 'material', 'tool', 'other']
        const priority = code => order.includes(code) ? order.indexOf(code) : order.length
        return structured.slice().sort((first, second) => priority(first.code) - priority(second.code))
      }
      const items = this.objectRows(this.report.items).filter(item => Number(item.groupType == null ? 1 : item.groupType) !== 2)
      return items.length ? [{ code: 'other', label: '原始基础评分', score: this.report.baseScore, maxScore: 80, items }] : []
    },
    selectedGroup() { return this.groups.find(group => group.code === this.selectedCode) },
    expressionGroup() { return this.groups.find(group => group.code === 'confidence') },
    suggestions() { return textSuggestions(this.report.suggestions) },
    coachSections() { return this.objectRows(this.report.coachSections) },
    flowSteps() { return this.objectRows(this.report.flowSteps) },
    visibleFlowSteps() { return this.showAllFlow ? this.flowSteps : this.flowSteps.slice(0, 3) },
    radarDetails() { return this.objectRows(this.report.radarDetails) },
    radarData() {
      const saved = toObject(this.report.radar)
      if (Object.keys(saved).length) return saved
      return this.radarDetails.filter(dimension => dimension.key && dimension.score != null)
        .reduce((data, dimension) => { data[dimension.key] = dimension.score; return data }, {})
    },
    hasRadar() { return Object.keys(this.radarData).length > 0 },
    radarMax() { return Math.max(5, ...this.radarDetails.map(dimension => Number(dimension.maxScore) || 0)) }
  },
  watch: { report() { this.selectGroup('') } },
  methods: {
    scoreText,
    textSuggestions,
    toArray,
    objectRows(value) { return toArray(value).filter(row => row && typeof row === 'object' && !Array.isArray(row)) },
    percentText(group) {
      return group.score == null || !Number.isFinite(Number(group.score)) || !(Number(group.maxScore) > 0)
        ? '' : '得分率' + Math.round(Number(group.score) / Number(group.maxScore) * 100) + '%'
    },
    groupIcon(code) {
      return { content: 'el-icon-notebook-2', process: 'el-icon-share', confidence: 'el-icon-user', material: 'el-icon-goods', tool: 'el-icon-setting' }[code] || 'el-icon-tickets'
    },
    selectGroup(code) {
      this.selectedCode = code
      this.showAllFlow = false
      this.$nextTick(() => {
        if (this.$refs.reportBody) this.$refs.reportBody.scrollTop = 0
        this.$emit('navigate')
      })
    }
  }
}
</script>

<style scoped>
.training-report { color: #303133; }
.report-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 0 8px 8px; }
.report-toolbar > strong { font-size: 16px; }
.report-paper { background: #fff; border: 1px solid #e4ece7; border-radius: 12px; padding: 24px; }
.score-summary { display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.score-label { font-size: 13px; color: #606266; margin-bottom: 6px; }
.score-value { font-size: 38px; font-weight: 700; color: #244f41; line-height: 1.2; white-space: nowrap; }
.score-value > span { font-size: 13px; font-weight: 400; color: #909399; margin-left: 10px; }
.score-stamp { font-size: 22px; color: #d9787b; border: 2px solid currentColor; border-radius: 4px; padding: 10px 16px; transform: rotate(-10deg); margin: 0 12px; }
.score-stamp-pass { color: #3b9c7c; }
.score-rule { font-size: 13px; color: #606266; line-height: 1.9; margin: 18px 0; }
.historical-tip { padding: 10px 14px; background: #f4f8f6; color: #778b82; line-height: 1.8; border-radius: 6px; font-size: 13px; }
.overview-columns { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 32px; margin-top: 24px; }
.score-groups { display: flex; flex-direction: column; gap: 12px; }
.score-group { display: flex; align-items: center; gap: 10px; width: 100%; border: 1px solid #e4eee8; border-radius: 8px; padding: 16px 12px; background: #f4faf7; cursor: pointer; color: #348569; text-align: left; font-size: 14px; }
.score-group:hover, .score-group:focus-visible { border-color: #54ad8e; background: #eaf7ef; }
.group-label { font-weight: 600; min-width: 0; overflow-wrap: anywhere; }
.group-score { margin-left: auto; color: #293b32; white-space: nowrap; font-weight: 600; }
.group-score small { font-size: 12px; font-weight: 400; color: #909399; }
.group-rate { font-size: 11px; color: #909399; white-space: nowrap; }
.coach h3, .overview-radar h3 { font-size: 15px; color: #348569; margin: 0 0 16px; }
.coach h4 { font-size: 14px; margin: 0 0 6px; }
.coach-section { margin-bottom: 18px; }
.coach p, .dimension-card p { margin: 6px 0; font-size: 14px; line-height: 1.9; white-space: pre-wrap; overflow-wrap: anywhere; }
.coach .coach-suggestion { margin-top: 8px; }
.overview-radar { margin-top: 28px; padding-top: 20px; border-top: 1px solid #edf2ee; }
.expression-link { display: block; margin: 8px auto 0; }
.report-navigation { border-bottom: 1px solid #e4e7ed; }
.overview-link { margin-left: 6px; }
.category-tabs { display: flex; gap: 24px; overflow-x: auto; padding: 0 6px; }
.category-tabs button { border: none; background: transparent; padding: 12px 0; font-size: 14px; color: #606266; cursor: pointer; white-space: nowrap; }
.category-tabs .category-active { color: #267a5c; font-weight: 600; border-bottom: 3px solid #65b596; }
.report-details { padding: 16px; background: #f4f7f5; border-radius: 0 0 8px 8px; }
.detail-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; font-size: 16px; margin: 0 0 18px; line-height: 1.8; }
.detail-heading > span { min-width: 0; overflow-wrap: anywhere; }
.detail-heading > strong { color: #348569; white-space: nowrap; }
.detail-heading small, .dimension-heading small { font-size: 12px; color: #909399; font-weight: 400; }
.flow-card, .radar-card, .dimension-card { background: #fff; padding: 16px; border-radius: 8px; margin: 12px 0; }
.flow-card > h4 { margin: 0 0 12px; font-size: 14px; }
.flow-row { display: grid; grid-template-columns: 50px minmax(0, 1fr) 80px; gap: 12px; padding: 12px 8px; border-bottom: 1px solid #eef2ef; line-height: 1.8; }
.flow-row > span { overflow-wrap: anywhere; }
.flow-head { background: #f5f8f6; color: #606266; font-weight: 600; }
.flow-missing { color: #cf777a; }
.flow-link { display: block; padding: 4px 0; }
.flow-toggle { display: block; margin: 14px auto 0; }
.dimension-heading { display: flex; justify-content: space-between; gap: 16px; border-bottom: 1px solid #e4e7ed; padding-bottom: 12px; line-height: 1.8; }
.dimension-heading > span { color: #348569; white-space: nowrap; }
.dimension-card h4 { color: #348569; margin: 14px 0 6px; font-size: 13px; }
.muted { color: #909399; }
.empty-text { padding: 16px 0; line-height: 1.8; }
@media (max-width: 900px) {
  .overview-columns { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .report-paper { padding: 16px; }
  .score-group { gap: 6px; }
}
</style>
